import {useCallback, useLayoutEffect, useMemo, useRef, useSyncExternalStore} from 'react'
import type {
  EstablishedAhpSessionView,
  EstablishedAhpSessionViewSnapshot,
} from '@github-ui/ahp-session-client/contracts'
import {
  completedCheckpoints,
  readCheckpoints,
  updateCheckpointAnchors,
  type CheckpointAnchors,
} from '@github-ui/ahp-relay/session-checkpoints'
import {useOptionalEstablishedAhpSession} from '@github-ui/copilot-shared-ui/hooks/use-established-ahp-session'
import type {AhpSessionBundle} from '../../../utils/replay-ahp-events'
import {
  COMPACTION_MARKER_ANCHOR,
  compactionAnchorTurns,
  findCompactionCommandAnchor,
  projectCompactionMarkers,
} from './compaction-marker'

const EMPTY_ANCHORS: CheckpointAnchors = new Map()

interface ObservedCheckpointAnchor {
  afterTurnId: string | null
  live: boolean
}

const EMPTY_OBSERVATIONS: ReadonlyMap<number, ObservedCheckpointAnchor> = new Map()
const getEmptyObservations = () => EMPTY_OBSERVATIONS

interface CompactionCheckpointTracker {
  view: EstablishedAhpSessionView | undefined
  generation: number | undefined
  snapshot?: EstablishedAhpSessionViewSnapshot
  initialNumbers?: Set<number>
  liveNumbers: Set<number>
  unrecoverableNumbers: Set<number>
  anchors: CheckpointAnchors
  observations: ReadonlyMap<number, ObservedCheckpointAnchor>
}

function mergeCheckpointAnchors(
  anchors: CheckpointAnchors,
  liveNumbers: ReadonlySet<number>,
  persisted: CheckpointAnchors | undefined,
): CheckpointAnchors {
  if (!persisted?.size) return anchors
  const merged = new Map([...anchors, ...persisted])
  for (const number of liveNumbers) {
    const anchor = anchors.get(number)
    if (anchor !== undefined) merged.set(number, anchor)
  }
  return merged
}

function trackCompactionSnapshot(
  tracker: CompactionCheckpointTracker,
  snapshot: EstablishedAhpSessionViewSnapshot,
  bundle: AhpSessionBundle | undefined,
): void {
  if (tracker.snapshot === snapshot) return
  tracker.snapshot = snapshot
  const checkpoints = readCheckpoints(snapshot.sessionState?._meta)
  const numbers = new Set(completedCheckpoints(checkpoints).map(checkpoint => checkpoint.checkpointNumber))
  if (snapshot.sessionState) {
    tracker.initialNumbers ??= new Set(numbers)
  }
  if (checkpoints === undefined) return
  for (const number of tracker.initialNumbers ?? []) {
    if (!numbers.has(number)) tracker.initialNumbers?.delete(number)
  }
  for (const number of tracker.liveNumbers) {
    if (!numbers.has(number)) tracker.liveNumbers.delete(number)
  }
  for (const number of tracker.unrecoverableNumbers) {
    if (!numbers.has(number)) tracker.unrecoverableNumbers.delete(number)
  }
  const newNumbers = [...numbers].filter(
    number =>
      !tracker.initialNumbers?.has(number) && !tracker.anchors.has(number) && !tracker.unrecoverableNumbers.has(number),
  )
  if (newNumbers.length > 1 || !snapshot.state) {
    for (const number of newNumbers) tracker.unrecoverableNumbers.add(number)
  }
  if (
    tracker.anchors.size + tracker.unrecoverableNumbers.size === numbers.size &&
    [...numbers].every(number => tracker.anchors.has(number) || tracker.unrecoverableNumbers.has(number))
  )
    return
  const state = snapshot.state
  const turns = compactionAnchorTurns(state, bundle)
  const initialMarkers = projectCompactionMarkers(checkpoints, turns)
  const fallback =
    state?.activeTurn?.id ??
    state?.turns.at(-1)?.id ??
    (state && state.turnsNextCursor === undefined ? null : undefined)
  const nextAnchors = updateCheckpointAnchors(tracker.anchors, checkpoints, (number, anchors) => {
    if (!state || tracker.unrecoverableNumbers.has(number)) return undefined
    if (tracker.initialNumbers?.has(number)) {
      const marker = initialMarkers.find(entry => entry.checkpointNumber === number)
      if (marker && marker.afterTurnId !== COMPACTION_MARKER_ANCHOR) {
        const turn = turns.find(entry => entry.id === marker.afterTurnId)
        if (turn && 'state' in turn) return marker.afterTurnId
        return undefined
      }
      return fallback
    }
    const knownAnchors = new Map(mergeCheckpointAnchors(anchors, tracker.liveNumbers, bundle?.checkpointAnchors))
    knownAnchors.delete(number)
    const anchor = findCompactionCommandAnchor(turns, knownAnchors) ?? fallback
    if (anchor !== undefined) tracker.liveNumbers.add(number)
    return anchor
  })
  if (nextAnchors !== tracker.anchors) {
    tracker.anchors = nextAnchors
    tracker.observations = new Map(
      [...nextAnchors].map(([number, afterTurnId]) => [number, {afterTurnId, live: tracker.liveNumbers.has(number)}]),
    )
  }
}

export function useCompactionCheckpointAnchors(bundle?: AhpSessionBundle): CheckpointAnchors {
  const view = useOptionalEstablishedAhpSession()?.view
  const generation = view?.generation
  const bundleRef = useRef(bundle)
  useLayoutEffect(() => {
    bundleRef.current = bundle
  }, [bundle])
  const trackerRef = useRef<CompactionCheckpointTracker | undefined>(undefined)
  const subscribe = useCallback(
    (onChange: () => void) => {
      if (!view) {
        trackerRef.current = undefined
        return () => undefined
      }
      const previousTracker = trackerRef.current
      const tracker: CompactionCheckpointTracker =
        previousTracker?.view === view && previousTracker.generation === generation
          ? previousTracker
          : {
              view,
              generation,
              liveNumbers: new Set(),
              unrecoverableNumbers: new Set(),
              anchors: EMPTY_ANCHORS,
              observations: EMPTY_OBSERVATIONS,
            }
      trackerRef.current = tracker
      const update = () => {
        const previous = tracker.observations
        trackCompactionSnapshot(tracker, view.getSnapshot(), bundleRef.current)
        if (tracker.observations !== previous) onChange()
      }
      const unsubscribe = view.subscribe(update)
      update()
      return unsubscribe
    },
    [view, generation],
  )
  const getSnapshot = useCallback(() => {
    const tracker = trackerRef.current
    return tracker && tracker.view === view && tracker.generation === generation
      ? tracker.observations
      : EMPTY_OBSERVATIONS
  }, [view, generation])
  const observations = useSyncExternalStore(subscribe, getSnapshot, getEmptyObservations)
  return useMemo(() => {
    const anchors = new Map(bundle?.checkpointAnchors)
    for (const [number, observation] of observations) {
      if (observation.live || !anchors.has(number)) anchors.set(number, observation.afterTurnId)
    }
    return anchors
  }, [observations, bundle?.checkpointAnchors])
}
